import { hasSubtle } from "./sha256";

/**
 * The PIN, and what it is not.
 *
 * It is a convenience lock on a phone that is already signed in, not an
 * authentication factor: the real session is the server's cookie and the PIN
 * never reaches the server (ADR 0004). So a forgotten PIN costs a password
 * re-entry and nothing else.
 *
 * It is derived with PBKDF2 where the platform offers it. On an insecure origin
 * (http on a LAN address) crypto.subtle does not exist, and rather than store a
 * plain hash and pretend, the feature turns itself off and says so.
 */

const KEY = "gpl.pin.v1";
const ITERATIONS = 150_000;

type Stored = { salt: string; hash: string; iter: number };

const toHex = (b: Uint8Array) => [...b].map((x) => x.toString(16).padStart(2, "0")).join("");

async function derive(pin: string, salt: Uint8Array, iter: number): Promise<string> {
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(pin), "PBKDF2", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: salt as unknown as BufferSource, iterations: iter, hash: "SHA-256" },
    key,
    256,
  );
  return toHex(new Uint8Array(bits));
}

export const pinSupported = () => hasSubtle() && typeof localStorage !== "undefined";

export function hasPin(): boolean {
  try {
    return !!localStorage.getItem(KEY);
  } catch {
    return false;
  }
}

export async function setPin(pin: string): Promise<void> {
  if (!pinSupported()) throw new Error("PIN tidak tersedia di koneksi tidak aman.");
  if (!/^\d{6}$/.test(pin)) throw new Error("PIN harus 6 angka.");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(pin, salt, ITERATIONS);
  localStorage.setItem(KEY, JSON.stringify({ salt: toHex(salt), hash, iter: ITERATIONS } satisfies Stored));
}

export async function checkPin(pin: string): Promise<boolean> {
  if (!pinSupported()) return false;
  const raw = localStorage.getItem(KEY);
  if (!raw) return false;
  try {
    const s = JSON.parse(raw) as Stored;
    const salt = new Uint8Array(s.salt.match(/.{2}/g)!.map((h) => parseInt(h, 16)));
    // Constant-time is not the bar here: the attacker already has the device and
    // the stored hash, and the comparison is local. This is a lock, not a secret.
    return (await derive(pin, salt, s.iter)) === s.hash;
  } catch {
    return false;
  }
}

export function clearPin(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
